import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';

import camelCase from 'lodash/camelCase';

import Tooltip from 'components/Tooltip';
import StatusLabel from 'components/status-label/StatusLabel';
import CheckboxesList from 'components/inputs/checkboxes-list/CheckboxesList';
import CreatorInfoWithChannels from 'components/creator-info-with-channels/CreatorInfoWithChannels';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import DeliverableTagPill from 'components/deliverable-tag-pill/DeliverableTagPill';

import { isDefined } from 'utils/helper/common';
import { DELIMITER_OVERRIDE, getDisplayDateRange, getDisplayDatesInterval } from 'utils/date';
import { DELIVERABLE_ICONS, sortDeliverables, sortNewDeliverables } from 'utils/deliverables';
import { __t, applyTranslations } from 'utils/language';

import type {
  CheckboxesListItem,
  CustomReportDeliverable,
  CustomReportOffer,
  InfluencerMetrics,
  StatusLabelType } from 'types';
import type { SyntheticEvent } from 'react';

import './subcampaigns-deliverables-rows.scss';

applyTranslations(require('./subcampaigns-deliverables-rows.i18n.json'));

type OwnProps = {
  campaignId: number,
  visibleGroupedDeliverables: Array<CustomReportOffer>,
  allGroupedDeliverables: Array<CustomReportOffer>,
  checkedDeliverables: Array<number>,
  expandedIds: Array<number>,
  isZalandoOutfitOnly: boolean,
  itemClassName?: string,
  includeRatio?: boolean,
  contentApprovalV2?: boolean,
  includeLocation?: boolean,
  onToggleExpanded: (a: SyntheticEvent<HTMLElement>) => void,
  onToggleCreatorSelection: (creatorId: number, isChecked: boolean) => void,
  onToggleDeliverableSelection: (creatorId: number, deliverableId: number | string) => void,
  getTooltipForDisabledDeliverable?: (deliverable: CustomReportDeliverable) => string
};

type StateProps = {
  influencersMetrics: InfluencerMetrics
}

type Props = OwnProps & StateProps;

class SubcampaignsDeliverablesRowsCustom extends React.Component<Props> {
  handleListItemClick = (
    event: SyntheticEvent<HTMLElement>,
    item: CheckboxesListItem,
    creatorId: number
  ): void => {
    const { onToggleDeliverableSelection } = this.props;

    if (!item.isDisabled) {
      onToggleDeliverableSelection(creatorId, item.id);
    }
  };

  handleCreatorSelect = (creatorId: number, isChecked: boolean): void => {
    const { onToggleCreatorSelection } = this.props;

    onToggleCreatorSelection(creatorId, isChecked);
  };

  getTooltipForDeliverable(item: CustomReportDeliverable): string {
    const { getTooltipForDisabledDeliverable } = this.props;

    if (item.selectable) {
      return '';
    }

    return getTooltipForDisabledDeliverable ? getTooltipForDisabledDeliverable(item) : '';
  }

  isDeliverableChecked(item: CustomReportDeliverable): boolean {
    const { checkedDeliverables } = this.props;

    return item.selectable && checkedDeliverables.includes(item.id);
  }

  isCreatorHasCheckedDeliverables(creatorId: number): boolean {
    const { allGroupedDeliverables } = this.props;
    const groupDeliverables = allGroupedDeliverables.find((group) => group.id === creatorId)?.deliverables || [];

    return groupDeliverables.filter(this.isDeliverableChecked, this).length > 0;
  }

  renderStatus(deliverable: CustomReportDeliverable): React.ReactNode {
    const statusToTypeMap: { [x: string]: StatusLabelType } = {
      pending: 'warning',
      done: 'success',
      overdue: 'danger'
    };
    const status = deliverable.status;

    if (!status) {
      return null;
    }

    return (
      <StatusLabel
        size="nano"
        type={statusToTypeMap[status]}
        label={__t(`subcampaigns_deliverables_rows.status.${status}`)}
      />
    );
  }

  renderTooltip = (deliverable: CustomReportDeliverable, subcampaignId: number): JSX.Element => {
    const { campaignId } = this.props;
    if (deliverable.additional) {
      return (
        <Tooltip message={__t('subcampaigns_deliverables_rows.additional_task_tooltip')}>
          {deliverable.name}*
        </Tooltip>
      );
    }

    const nameClasses = classnames(
      'subcampaigns-deliverables-rows__task-name',
      'text-overflow-ellipsis',
      {
        'subcampaigns-deliverables-rows__task-name--shortened': deliverable.contentApprovalV2
      }
    );

    return (
      <Tooltip message={__t('subcampaigns_deliverables_rows.task_name_tooltip')}>
        <a
          href={`/campaigns/${campaignId}/subcampaigns/${subcampaignId}#deliverable-${deliverable.id}`}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          target="_blank"
          rel="noopener noreferrer"
          className={nameClasses}
        >
          {deliverable.name}
        </a>
      </Tooltip>
    );
  };

  renderLabel = (deliverable: CustomReportDeliverable, subcampaignId: number): JSX.Element => {
    const { isZalandoOutfitOnly } = this.props;
    const type = deliverable.deliverableType;
    const tag = deliverable.deliverableTag || deliverable.tag;
    const tagName = tag?.name;
    const tagColor = tag?.color;
    const nameClassNames = classnames(
      'subcampaigns-deliverables-rows__name',
      'subcampaigns-deliverables-rows__name--custom-report',
      {
        'subcampaigns-deliverables-rows__name--compact': deliverable.contentApprovalV2
      }
    );

    return (
      <div className="subcampaigns-deliverables-rows__label">
        <div className={nameClassNames}>
          <div className="subcampaigns-deliverables-rows__icon">
            <SVGDynamicIcon
              name={`${DELIVERABLE_ICONS[camelCase(type)]}`}
              width={16}
              height={16}
            />
          </div>
          <div className="subcampaigns-deliverables-rows__custom-report-task">
            {this.renderTooltip(deliverable, subcampaignId)}
            <Tooltip
              showQuestionMark
              message={this.getTooltipForDeliverable(deliverable)}
              // width="long"
            />
          </div>
          {tagName && tagColor && (
            <div className="subcampaigns-deliverables-rows__tag">
              <DeliverableTagPill
                name={tagName}
                color={tagColor}
                size="nano"
              />
            </div>
          )}
        </div>
        {deliverable.contentApprovalV2 && (
          <div className="subcampaigns-deliverables-rows__ca2-date-info">
            <span>
              {
                getDisplayDatesInterval(
                  deliverable.contentApprovalTimeline?.startDate,
                  deliverable.contentApprovalTimeline?.endDate,
                  DELIMITER_OVERRIDE
                )
              }
            </span>
            {
              !isZalandoOutfitOnly && (
                <React.Fragment>
                  <span>
                    {
                      getDisplayDatesInterval(
                        deliverable.contentGoLiveTimeline?.startDate,
                        deliverable.contentGoLiveTimeline?.endDate,
                        DELIMITER_OVERRIDE
                      )
                    }
                  </span>
                  <span>
                    {
                      getDisplayDatesInterval(
                        deliverable.insightsProvisionTimeline?.startDate,
                        deliverable.insightsProvisionTimeline?.endDate,
                        DELIMITER_OVERRIDE
                      )
                    }
                  </span>
                </React.Fragment>
              )
            }
          </div>
        )}
        {!deliverable.contentApprovalV2 && (
          <React.Fragment>
            <div className="subcampaigns-deliverables-rows__date">
              {getDisplayDateRange(deliverable.startDate, deliverable.endDate)}
            </div>
            <div className="subcampaigns-deliverables-rows__status">
              {this.renderStatus(deliverable)}
            </div>
          </React.Fragment>
        )}
      </div>
    );
  };

  renderGroup = (group: CustomReportOffer): JSX.Element | null => {
    const {
      contentApprovalV2,
      expandedIds,
      onToggleExpanded,
      itemClassName,
      influencersMetrics,
      includeRatio,
      isZalandoOutfitOnly,
      includeLocation
    } = this.props;

    if (!group.deliverables.length) {
      return null;
    }

    const creatorProfile = {
      id: group.id,
      name: group.name,
      profileImage: group.profileImage,
      channels: group.channels,
      influencerFirstName: group.influencerFirstName,
      influencerLastName: group.influencerLastName,
      country: group.country
    };
    const creatorId = creatorProfile?.id;

    const isExpanded = expandedIds?.includes(creatorId);
    const isChecked = group.deliverables.some((item: CustomReportDeliverable): boolean => this.isDeliverableChecked(item));
    const isPartiallyChecked = !isChecked && this.isCreatorHasCheckedDeliverables(creatorId);

    const wrapperClassNames = classnames('subcampaigns-deliverables-rows', itemClassName);
    const checkboxWrapperClassNames = classnames('subcampaigns-deliverables-rows__checkbox', {
      'subcampaigns-deliverables-rows__checkbox--opaque': isPartiallyChecked
    });
    const checkboxTooltipMessage = (isPartiallyChecked)
      ? __t('subcampaigns_deliverables_rows.partially_selected_tooltip')
      : '';
    const hasCA2 = group.deliverables.some(deliverable => deliverable.contentApprovalV2);
    const sortedDeliverableList = contentApprovalV2
      ? sortNewDeliverables(group.deliverables, 'start_date_asc')
      : sortDeliverables(group.deliverables);
    const deliverables = sortedDeliverableList
      .map(deliverable => ({
        ...deliverable,
        isSelected: this.isDeliverableChecked(deliverable),
        isDisabled: !deliverable.selectable
      }));

    return creatorProfile && (
      <div
        className={wrapperClassNames}
        key={`subcampaigns-deliverables-rows-${creatorId}`}
      >
        <div
          className="subcampaigns-deliverables-rows__collapse-icon"
          onClick={onToggleExpanded}
          data-key={creatorId}
        >
          <SVGDynamicIcon
            name={`chevron-${isExpanded ? 'up' : 'down'}-custom-fg`}
            width={12}
            height={12}
          />
        </div>
        <Tooltip
          key={`partially-checked-tooltip-${String(isPartiallyChecked)}`}
          width="long"
          message={checkboxTooltipMessage}
        >
          <InputCheckbox
            id={`subcampaign-deliverable-row-${creatorId}`}
            containerClass={checkboxWrapperClassNames}
            type="filter"
            checked={isChecked || isPartiallyChecked}
            onChange={() => this.handleCreatorSelect(creatorId, !isChecked)}
          />
        </Tooltip>
        <section className="subcampaigns-deliverables-rows__expandable-content">
          <div className="subcampaigns-deliverables-rows__content">
            <div className="subcampaigns-deliverables-rows__group-header">
              <CreatorInfoWithChannels
                creatorProfile={creatorProfile}
                metrics={influencersMetrics[creatorProfile.id]}
                includeRatio={includeRatio}
                includeLocation={includeLocation}
              />
              {
                hasCA2 && isExpanded && (
                  <div className="subcampaigns-deliverables-rows__ca2-header-info">
                    <span>Content approval</span>
                    {
                      !isZalandoOutfitOnly && (
                        <React.Fragment>
                          <span>Go live</span>
                          <span>Insights provision</span>
                        </React.Fragment>
                      )
                    }
                  </div>
                )
              }
            </div>
            {
              isExpanded && (
                <div className="subcampaigns-deliverables-rows__list">
                  <CheckboxesList
                    emptyLabel=""
                    lists={deliverables}
                    labelComponent={(deliverable: CustomReportDeliverable) => this.renderLabel(deliverable, group.subcampaignId)}
                    onCheckboxChange={(event, item) => this.handleListItemClick(event, item, group.id)}
                  />
                </div>
              )
            }
          </div>
        </section>
      </div>
    );
  };

  render(): React.ReactNode {
    const { visibleGroupedDeliverables } = this.props;

    const groups = visibleGroupedDeliverables.map<React.ReactNode>(
      group => this.renderGroup(group)
    );

    if (!groups.filter(isDefined).length) {
      return (
        <div className="subcampaigns-deliverables-rows__filter">
          {__t('subcampaigns_deliverables_rows.empty_filter')}
        </div>
      );
    }

    return groups;
  }
}

const mapStateToProps = ({ influencersMetrics }): StateProps => ({
  influencersMetrics: influencersMetrics.data
});

export default connect(mapStateToProps)(SubcampaignsDeliverablesRowsCustom);
